import { GasType } from '../GasType'
import {
  GhgEmissionCalculationMaterial,
  GhgEmissionFuelEfficiencyCalculationMaterial,
  VehicleUsageType,
} from '../GhgEmissionCalculationMaterial'
import { FuelEfficiencyTransportType } from '../TransportType'

// Referring to government organization (経済産業省、国土交通省) in japan
// https://www.mlit.go.jp/common/001037031.pdf Page.5
// distance (kilo meter) / fuel efficiency (kilo meter / litter) × 1/1000 (kilo litter / litter)
//  × unit calorific value (Giga joule / kilo litter)
//  × gas emission coefficient (ton-Gas weight / Giga joule)
//  => gas weight(ton)
// 輸送距離(km) / 燃費(km / l) x 1/1000 (kl / l) × 単位発熱量(Gj / kl) × ガス排出量係数(t / Gj)
//  => ガス排出量(t)
export const calcFuelEfficiencyEmission = (
  kilometer: number,
  fuelEfficiency: number,
  unitCalorificValue: number,
  gasEmissionCoefficient: number
) => (kilometer / fuelEfficiency / 1000) * unitCalorificValue * gasEmissionCoefficient

// https://www.mlit.go.jp/common/001037031.pdf Page.5
const businessVehicleFuelEfficiencyMap: Map<FuelEfficiencyTransportType, number> = new Map([
  ['gasoline-light-cargo', 9.33],
  ['gasoline-lt-2t', 6.57],
  ['gasoline-gte-2t', 4.96],
  ['light-oil-lt-1t', 9.32],
  ['light-oil-gte-1t-lt-2t', 6.19],
  ['light-oil-gte-2t-lt-4t', 4.58],
  ['light-oil-gte-4t-lt-6t', 3.79],
  ['light-oil-gte-6t-lt-8t', 3.38],
  ['light-oil-gte-8t-lt-10t', 3.09],
  ['light-oil-gte-10t-lt-12t', 2.89],
  ['light-oil-gte-12t-lt-17t', 2.62],
])

// https://www.mlit.go.jp/common/001037031.pdf Page.5
const privateVehicleFuelEfficiencyMap: Map<FuelEfficiencyTransportType, number> = new Map([
  ['gasoline-light-cargo', 10.3],
  ['gasoline-lt-2t', 7.15],
  ['gasoline-gte-2t', 5.25],
  ['light-oil-lt-1t', 11.9],
  ['light-oil-gte-1t-lt-2t', 7.34],
  ['light-oil-gte-2t-lt-4t', 4.94],
  ['light-oil-gte-4t-lt-6t', 3.96],
  ['light-oil-gte-6t-lt-8t', 3.53],
  ['light-oil-gte-8t-lt-10t', 3.23],
  ['light-oil-gte-10t-lt-12t', 3.02],
  ['light-oil-gte-12t-lt-17t', 2.74],
])

function getFuelEfficiency(transportType: FuelEfficiencyTransportType, vehicleUsageType: VehicleUsageType) {
  switch (vehicleUsageType) {
    case 'business':
      return businessVehicleFuelEfficiencyMap.get(transportType) ?? 0
    case 'private':
      return privateVehicleFuelEfficiencyMap.get(transportType) ?? 0
  }
}

function calcCo2UnitCalorificValue(transportType: FuelEfficiencyTransportType) {
  switch (transportType) {
    case 'gasoline-light-cargo':
    case 'gasoline-lt-2t':
    case 'gasoline-gte-2t':
      // when gasoline engine
      // This value will be updated periodically. Use latest one.
      // https://www.mlit.go.jp/common/001037031.pdf Page.4
      // https://www.enecho.meti.go.jp/category/saving_and_new/saving/enterprise/transport/institution/ninushi_santeikokuji.pdf  Page.4
      // https://elaws.e-gov.go.jp/document?lawid=411CO0000000143_20230401_505CO0000000068
      return 34.6
    case 'light-oil-lt-1t':
    case 'light-oil-gte-1t-lt-2t':
    case 'light-oil-gte-2t-lt-4t':
    case 'light-oil-gte-4t-lt-6t':
    case 'light-oil-gte-6t-lt-8t':
    case 'light-oil-gte-8t-lt-10t':
    case 'light-oil-gte-10t-lt-12t':
    case 'light-oil-gte-12t-lt-17t':
      // when diesel engine (light oil)
      // This value will be updated periodically. Use latest one.
      // https://www.mlit.go.jp/common/001037031.pdf Page.4
      // https://www.enecho.meti.go.jp/category/saving_and_new/saving/enterprise/transport/institution/ninushi_santeikokuji.pdf Page.4
      // https://elaws.e-gov.go.jp/document?lawid=411CO0000000143_20230401_505CO0000000068
      return 37.7
  }
}

function calcCarbonEmissionCoefficient(transportType: FuelEfficiencyTransportType) {
  switch (transportType) {
    case 'gasoline-light-cargo':
    case 'gasoline-lt-2t':
    case 'gasoline-gte-2t':
      // when gasoline engine
      // This value will be updated periodically. Use latest one.
      // https://www.mlit.go.jp/common/001037031.pdf Page.4
      // https://elaws.e-gov.go.jp/document?lawid=411CO0000000143_20230401_505CO0000000068
      return 0.0183
    case 'light-oil-lt-1t':
    case 'light-oil-gte-1t-lt-2t':
    case 'light-oil-gte-2t-lt-4t':
    case 'light-oil-gte-4t-lt-6t':
    case 'light-oil-gte-6t-lt-8t':
    case 'light-oil-gte-8t-lt-10t':
    case 'light-oil-gte-10t-lt-12t':
    case 'light-oil-gte-12t-lt-17t':
      // when diesel engine (light oil)
      // This value will be updated periodically. Use latest one.
      // https://www.mlit.go.jp/common/001037031.pdf Page.4
      // https://elaws.e-gov.go.jp/document?lawid=411CO0000000143_20230401_505CO0000000068
      return 0.0187
  }
}

function convertToCalcMaterial(material: unknown) {
  const {
    transportType,
    calculationType,
    gasTypes,
    distance,
    vehicleUsageType,
  } = material as GhgEmissionFuelEfficiencyCalculationMaterial
  return {
    transportType,
    calculationType,
    gasTypes,
    distance,
    vehicleUsageType,
  } as GhgEmissionFuelEfficiencyCalculationMaterial
}
export function calcFuelEfficiencyCo2Emission(material: GhgEmissionCalculationMaterial) {
  const calcMaterial = convertToCalcMaterial(material)
  const transportType = calcMaterial.transportType as FuelEfficiencyTransportType
  const fuelEfficiency = getFuelEfficiency(transportType, calcMaterial.vehicleUsageType)
  const unitCalorificValue = calcCo2UnitCalorificValue(transportType)
  // CO_2 molecular weight (44) / Carbon molecular weight (12)
  const co2EmissionCoefficient = calcCarbonEmissionCoefficient(transportType) * (44 / 12)
  return calcFuelEfficiencyEmission(calcMaterial.distance, fuelEfficiency, unitCalorificValue, co2EmissionCoefficient)
}

export function getFuelEfficiencyEmissionMap(material: GhgEmissionCalculationMaterial) {
  const coefficientMap = new Map<GasType, number>()
  material.gasTypes.forEach((gasType) => {
    switch (gasType) {
      case 'CO_2':
        coefficientMap.set(gasType, calcFuelEfficiencyCo2Emission(material))
        break
      case 'CH_4':
      case 'N_2O':
      default:
        coefficientMap.set(gasType, 0) // not supported yet
    }
  })
  return coefficientMap
}
